import { format, isValid, parse, parseISO } from 'date-fns';

/**
 * Get date range for "This Month"
 */
export const getThisMonthRange = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();

  // Create dates in UTC to avoid timezone issues
  const dateFrom = new Date(Date.UTC(year, month, 1));
  const dateTo = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));

  return {
    dateFrom: formatDateToUTC(dateFrom),
    dateTo: formatDateToUTC(dateTo),
  };
};

/**
 * Get date range for "Last Month"
 */
export const getLastMonthRange = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();

  // Create dates in UTC to avoid timezone issues
  const dateFrom = new Date(Date.UTC(year, month - 1, 1));
  const dateTo = new Date(Date.UTC(year, month, 0, 23, 59, 59, 999));

  return {
    dateFrom: formatDateToUTC(dateFrom),
    dateTo: formatDateToUTC(dateTo),
  };
};

/**
 * Get date range for "1 Year"
 */
export const getOneYearRange = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const day = now.getUTCDate();

  // Create dates in UTC to avoid timezone issues
  const dateTo = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
  const dateFrom = new Date(Date.UTC(year - 1, month, day));

  return {
    dateFrom: formatDateToUTC(dateFrom),
    dateTo: formatDateToUTC(dateTo),
  };
};

/** Common date formats to try when parseISO fails (e.g. mm/dd/yyyy, dd/mm/yyyy) */
const FALLBACK_FORMATS = [
  'yyyy-MM-dd',
  'MM/dd/yyyy',
  'dd/MM/yyyy',
  'M/d/yyyy',
  'd/M/yyyy',
] as const;

/**
 * Parse a date string with automatic format detection using date-fns.
 * Tries parseISO first (ISO 8601: "2026-03-08", "2026-03-08T00:00:00.000Z"),
 * then falls back to common formats (yyyy-MM-dd, MM/dd/yyyy, dd/MM/yyyy, etc).
 */
export const parseFlexibleDate = (dateStr?: string): Date | undefined => {
  if (!dateStr || typeof dateStr !== 'string') return undefined;
  const trimmed = dateStr.trim();
  if (!trimmed) return undefined;

  const parsedISO = parseISO(trimmed);
  if (isValid(parsedISO)) return parsedISO;

  const ref = new Date();
  for (const fmt of FALLBACK_FORMATS) {
    try {
      const parsed = parse(trimmed, fmt, ref);
      if (isValid(parsed)) return parsed;
    } catch {
      // Try next format
    }
  }
  return undefined;
};

/**
 * Normalize a date string to YYYY-MM-DD format.
 * Accepts ISO 8601, yyyy-MM-dd, mm/dd/yyyy, dd/mm/yyyy and other common formats.
 * Use when receiving dates from APIs for components that expect YYYY-MM-DD.
 */
export const toYYYYMMDD = (dateStr?: string): string | undefined => {
  const date = parseFlexibleDate(dateStr);
  if (!date) return undefined;
  return format(date, 'yyyy-MM-dd');
};

/**
 * Format date to YYYY-MM-DD format in UTC
 */
export const formatDateToUTC = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format date to YYYY-MM-DD format from local date string
 */
export const formatLocalDateToUTC = (dateString: string): string => {
  const date = new Date(dateString);
  return formatDateToUTC(date);
};

/**
 * Get date range for the previous calendar year (Jan 1 – Dec 31, UTC)
 */
export const getLastYearRange = () => {
  const now = new Date();
  const lastYear = now.getUTCFullYear() - 1;
  const dateFrom = new Date(Date.UTC(lastYear, 0, 1));
  const dateTo = new Date(Date.UTC(lastYear, 11, 31, 23, 59, 59, 999));

  return {
    dateFrom: formatDateToUTC(dateFrom),
    dateTo: formatDateToUTC(dateTo),
  };
};

/**
 * Get end date for "Last Year" (December 31 of last year)
 */
export const getLastYearEndDate = (): string => {
  const now = new Date();
  const lastYear = now.getUTCFullYear() - 1;
  const dateTo = new Date(Date.UTC(lastYear, 11, 31, 23, 59, 59, 999)); // December 31 of last year

  return formatDateToUTC(dateTo);
};

/**
 * Get date range for "This Year" (January 1 of current year to today)
 */
export const getThisYearRange = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const day = now.getUTCDate();

  // Create dates in UTC to avoid timezone issues
  const dateFrom = new Date(Date.UTC(year, 0, 1)); // January 1 of current year
  const dateTo = new Date(Date.UTC(year, month, day, 23, 59, 59, 999)); // Today

  return {
    dateFrom: formatDateToUTC(dateFrom),
    dateTo: formatDateToUTC(dateTo),
  };
};
